if (!customElements.get('warranty-register')) {
  const TURNSTILE_TIMEOUT_MS = 20000;
  const TURNSTILE_TIMEOUT_MESSAGE =
    'Security verification timed out. Please reload the page and try again, or email support@straum.co and we will register your watch for you.';
  const TURNSTILE_FAILED_MESSAGE =
    'Security verification failed. Please reload the page and try again, or email support@straum.co and we will register your watch for you.';

  const GENERIC_ERROR_MESSAGE = 'Registration failed. Please try again.';

  // The Edge Function answers a validation failure with
  // { ok: false, errors: { <payload field>: <message> } } and a 400. These are
  // the words the form uses for those payload fields, so a rejected field is
  // named the way the customer sees it. A field that is not in this map is not
  // one the customer can correct (submission_id, mode) — those fall back to the
  // generic message rather than showing an internal field name.
  const FIELD_LABELS = {
    serial_number: 'Serial number',
    model: 'Watch model',
    model_other: 'Watch model',
    purchase_date: 'Date you received your watch',
    full_name: 'Full name',
    email: 'Email address',
    country: 'Country',
    phone: 'Phone',
    address: 'Service address',
    retailer_name: 'Retailer',
    retailer_other_name: 'Retailer name',
    retailer_staff_name: 'Your name (staff)',
    shopify_order_ref: 'Order number',
  };

  // Marks a field the customer has to correct, as the support form does: the
  // message goes in the error area above the submit button and the field's
  // border turns red until it is edited.
  const FIELD_ERROR_CLASS = 'warranty-register__field--error';

  class WarrantyRegister extends HTMLElement {
    connectedCallback() {
      this.formState = parseInt(this.dataset.formState, 10);
      this.endpoint = this.dataset.endpoint;
      this.retailerMode = false;
      this.manualMode = false;
      this.turnstileWidgetId = null;
      this.turnstileResolve = null;
      this.turnstileReject = null;
      this._turnstileTimeout = null;

      this.form = this.querySelector('[data-form]');
      this.formArea = this.querySelector('[data-form-area]');
      this.submitBtn = this.querySelector('[data-submit]');
      this.errorsEl = this.querySelector('[data-errors]');
      this.successEl = this.querySelector('[data-success]');

      this.submissionIdInput = this.querySelector('[data-submission-id]');

      this._handleSubmit = this.handleSubmit.bind(this);
      this._handleRetailerToggle = this.toggleRetailer.bind(this);
      this._debouncedSaveDraft = this._createDebouncedSave();

      this.form.addEventListener('submit', this._handleSubmit);
      this.querySelector('[data-retailer-toggle]')?.addEventListener('click', this._handleRetailerToggle);

      const manualLink = this.querySelector('[data-manual-entry]');
      if (manualLink) {
        this._handleManualEntry = (e) => {
          e.preventDefault();
          this.enterManualMode();
        };
        manualLink.addEventListener('click', this._handleManualEntry);
      }

      this.querySelectorAll('[name="watch_selection"]').forEach((radio) => {
        radio.addEventListener('change', this.handleWatchSelect.bind(this));
      });

      const modelSelect = this.querySelector('[data-model-select]');
      if (modelSelect) {
        modelSelect.addEventListener('change', this.handleModelChange.bind(this));
      }

      const retailerSelect = this.querySelector('[data-retailer-select]');
      if (retailerSelect) {
        retailerSelect.addEventListener('change', this.handleRetailerChange.bind(this));
      }

      this.form.addEventListener('input', this._debouncedSaveDraft);
      this.form.addEventListener('change', this._debouncedSaveDraft);

      this._handleFieldEdit = this._clearFieldError.bind(this);
      this.form.addEventListener('input', this._handleFieldEdit);
      this.form.addEventListener('change', this._handleFieldEdit);

      const draft = this._loadDraft();
      if (draft) {
        this.submissionIdInput.value = draft.submission_id || crypto.randomUUID();
        this._restoreDraft(draft);
      } else {
        this.submissionIdInput.value = crypto.randomUUID();
      }

      this._initTurnstile();
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this._handleSubmit);
      this.querySelector('[data-retailer-toggle]')?.removeEventListener('click', this._handleRetailerToggle);
      this.form?.removeEventListener('input', this._debouncedSaveDraft);
      this.form?.removeEventListener('change', this._debouncedSaveDraft);
      this.form?.removeEventListener('input', this._handleFieldEdit);
      this.form?.removeEventListener('change', this._handleFieldEdit);
      clearTimeout(this._saveDraftTimer);
      clearTimeout(this._turnstileTimeout);
      clearInterval(this._turnstileCheck);
    }

    // --- Turnstile ---

    _initTurnstile() {
      const sitekey = this.dataset.turnstileKey;
      if (!sitekey) return;

      if (typeof turnstile !== 'undefined') {
        this._renderTurnstile();
        return;
      }

      this._turnstileCheck = setInterval(() => {
        if (typeof turnstile !== 'undefined') {
          clearInterval(this._turnstileCheck);
          this._renderTurnstile();
        }
      }, 200);

      setTimeout(() => clearInterval(this._turnstileCheck), 10000);
    }

    _renderTurnstile() {
      const container = this.querySelector('[data-turnstile]');
      const sitekey = this.dataset.turnstileKey;
      if (!container || !sitekey) return;

      this.turnstileWidgetId = turnstile.render(container, {
        sitekey,
        size: 'flexible',
        execution: 'execute',
        callback: (token) => {
          this._settleTurnstile(token);
        },
        'error-callback': () => {
          this._failTurnstile(TURNSTILE_FAILED_MESSAGE);
        },
        'timeout-callback': () => {
          this._failTurnstile(TURNSTILE_FAILED_MESSAGE);
        },
        'expired-callback': () => {
          this._failTurnstile(TURNSTILE_FAILED_MESSAGE);
        },
      });
    }

    _settleTurnstile(token) {
      if (!this.turnstileResolve) return;
      const resolve = this.turnstileResolve;
      this._clearTurnstilePending();
      resolve(token);
    }

    _failTurnstile(message) {
      if (!this.turnstileReject) return;
      const reject = this.turnstileReject;
      this._clearTurnstilePending();
      reject(new Error(message));
    }

    _clearTurnstilePending() {
      clearTimeout(this._turnstileTimeout);
      this._turnstileTimeout = null;
      this.turnstileResolve = null;
      this.turnstileReject = null;
    }

    // Cloudflare can decide a submission needs an interactive challenge. If that
    // challenge never completes, neither the callback nor the error-callback
    // fires, so the promise has to time out on its own — otherwise the form waits
    // forever with no message. See TURNSTILE_TIMEOUT_MS.
    _executeTurnstile() {
      return new Promise((resolve, reject) => {
        if (this.turnstileWidgetId == null) {
          reject(new Error('Security verification not available. Please refresh the page and try again.'));
          return;
        }
        this.turnstileResolve = resolve;
        this.turnstileReject = reject;
        this._turnstileTimeout = setTimeout(() => {
          this._failTurnstile(TURNSTILE_TIMEOUT_MESSAGE);
        }, TURNSTILE_TIMEOUT_MS);
        turnstile.execute(this.turnstileWidgetId);
      });
    }

    // --- State transitions ---

    toggleRetailer() {
      if (this.retailerMode) {
        this._exitRetailerMode();
      } else {
        this._enterRetailerMode();
      }
    }

    _enterRetailerMode() {
      this.retailerMode = true;
      this.querySelector('[data-mode]').value = 'retailer';

      const toggleBtn = this.querySelector('[data-retailer-toggle]');
      toggleBtn.textContent = 'Switch back to personal registration.';

      this.querySelector('[data-retailer-fields]').hidden = false;
      this.querySelector('[data-retailer-customer-header]').hidden = false;

      const summary = this.querySelector('[data-watch-summary]');
      if (summary) summary.hidden = true;
      const picker = this.querySelector('[data-watch-picker]');
      if (picker) picker.hidden = true;

      this.querySelector('[data-serial-field]').hidden = false;
      this.querySelector('[data-watch-fields]').hidden = false;

      const customerFields = this.querySelector('[data-customer-fields]');
      customerFields.hidden = false;
      if (this.formState <= 2) {
        customerFields.querySelectorAll('input, textarea').forEach((el) => {
          el.value = '';
        });
      }

      const marketingConsent = this.querySelector('[data-marketing-consent]');
      if (marketingConsent) marketingConsent.hidden = true;

      this.querySelector('[data-retailer-fields] input, [data-retailer-fields] select')?.focus();
    }

    _exitRetailerMode() {
      this.retailerMode = false;
      this.querySelector('[data-mode]').value = 'customer';

      const toggleBtn = this.querySelector('[data-retailer-toggle]');
      toggleBtn.textContent = 'Registering on behalf of a customer? Switch to retailer mode.';

      this.querySelector('[data-retailer-fields]').hidden = true;
      this.querySelector('[data-retailer-customer-header]').hidden = true;

      const marketingConsent = this.querySelector('[data-marketing-consent]');
      if (marketingConsent) marketingConsent.hidden = false;

      if (!this.manualMode && this.formState <= 2) {
        const summary = this.querySelector('[data-watch-summary]');
        if (summary) summary.hidden = false;
        const picker = this.querySelector('[data-watch-picker]');
        if (picker) picker.hidden = false;

        if (this.formState === 2 && !this.querySelector('[name="watch_selection"]:checked')) {
          this.querySelector('[data-serial-field]').hidden = true;
        }

        this.querySelector('[data-watch-fields]').hidden = true;
        this.querySelector('[data-customer-fields]').hidden = true;
      }
    }

    enterManualMode() {
      this.manualMode = true;

      const summary = this.querySelector('[data-watch-summary]');
      if (summary) summary.hidden = true;

      this.querySelector('[data-serial-field]').hidden = false;
      this.querySelector('[data-watch-fields]').hidden = false;

      const customerFields = this.querySelector('[data-customer-fields]');
      customerFields.hidden = false;
      customerFields.querySelectorAll('input, textarea').forEach((el) => {
        el.value = '';
      });

      this.querySelector('[name="serial_number"]')?.focus();
    }

    // --- Watch selection (State 2) ---

    handleWatchSelect(e) {
      const radio = e.target;
      const modelInput = this.querySelector('[data-prefill="model"]');
      const dateInput = this.querySelector('[data-prefill="purchase_date"]');
      const orderRefInput = this.querySelector('[data-prefill="shopify_order_ref"]');

      if (modelInput) modelInput.value = radio.dataset.model;
      if (dateInput) dateInput.value = radio.dataset.purchaseDate;
      if (orderRefInput) orderRefInput.value = radio.dataset.orderRef || '';

      this.querySelector('[data-serial-field]').hidden = false;
      this.querySelector('[name="serial_number"]')?.focus();
    }

    // --- Model/retailer "Other" toggles ---

    handleModelChange(e) {
      const otherField = this.querySelector('[data-model-other]');
      if (otherField) {
        otherField.hidden = e.target.value !== 'Older / other';
      }
    }

    handleRetailerChange(e) {
      const otherField = this.querySelector('[data-retailer-other]');
      if (otherField) {
        otherField.hidden = e.target.value !== 'Other';
      }
    }

    // --- Form submission ---

    async handleSubmit(e) {
      e.preventDefault();
      this.clearError();

      if (!this._validate()) return;

      this._setLoading(true);

      try {
        const token = await this._executeTurnstile();
        const payload = this._buildPayload(token);

        // A network failure rejects with the browser's own wording ("Failed to
        // fetch", "Load failed"), which is not for the customer.
        const response = await fetch(this.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }).catch(() => {
          throw new Error(GENERIC_ERROR_MESSAGE);
        });

        if (!response.ok) {
          const data = await response.json().catch(() => null);
          throw this._responseError(data);
        }

        this._showSuccess(payload.email);
      } catch (error) {
        this.showErrors(error.fieldErrors || [error.message || GENERIC_ERROR_MESSAGE]);
        if (error.fieldKeys) this._markFields(error.fieldKeys);
        this._setLoading(false);
        if (this.turnstileWidgetId != null) {
          turnstile.reset(this.turnstileWidgetId);
        }
      }
    }

    // Turn a rejected response body into the error to show. Field errors from
    // the Edge Function are named against their fields and marked on them;
    // anything else (a 500, a 403 from Turnstile, an unreadable body, a failure
    // naming only fields the customer cannot correct) keeps the generic
    // message. The function's own `error` strings ("Internal server error",
    // "Invalid JSON") are written for logs, not customers, so none is shown.
    _responseError(data) {
      const errors = data && typeof data.errors === 'object' && data.errors ? data.errors : null;

      if (errors) {
        const keys = Object.keys(errors).filter((key) => FIELD_LABELS[key]);
        if (keys.length > 0) {
          const messages = keys.map((key) => `${FIELD_LABELS[key]}: ${errors[key]}`);
          const error = new Error(messages[0]);
          error.fieldErrors = messages;
          error.fieldKeys = keys;
          return error;
        }
      }

      return new Error(GENERIC_ERROR_MESSAGE);
    }

    // The visible input for a payload field, or null when the field is not on
    // screen (a prefilled value, or a section the current mode hides).
    _inputFor(name) {
      const el = this.querySelector(`[name="${name}"]`);
      return el && !el.closest('[hidden]') ? el : null;
    }

    // Mark each named field and move focus to the first one. Focus does not
    // scroll: the error area holding the messages has just been scrolled into
    // view, and the field points at it through aria-describedby so a screen
    // reader announces the message with the field.
    _markFields(names) {
      let first = null;
      names.forEach((name) => {
        const input = this._inputFor(name);
        if (!input) return;
        input.closest('.warranty-register__field')?.classList.add(FIELD_ERROR_CLASS);
        input.setAttribute('aria-invalid', 'true');
        if (this.errorsEl.id) input.setAttribute('aria-describedby', this.errorsEl.id);
        if (!first) first = input;
      });
      first?.focus({ preventScroll: true });
    }

    _clearFieldError(e) {
      const target = e.target;
      if (!target.name) return;
      // A radio group carries the mark on its first radio, not the one picked.
      const group = this.querySelectorAll(`[name="${target.name}"][aria-invalid]`);
      if (group.length === 0) return;
      target.closest('.warranty-register__field')?.classList.remove(FIELD_ERROR_CLASS);
      group.forEach((el) => {
        el.removeAttribute('aria-invalid');
        el.removeAttribute('aria-describedby');
      });
    }

    _clearFieldErrors() {
      this.querySelectorAll(`.${FIELD_ERROR_CLASS}`).forEach((el) => el.classList.remove(FIELD_ERROR_CLASS));
      this.querySelectorAll('[aria-invalid]').forEach((el) => {
        el.removeAttribute('aria-invalid');
        el.removeAttribute('aria-describedby');
      });
    }

    // Shows the first problem as the message and marks every field that has
    // one, so the customer sees all of them at once.
    _validate() {
      const errors = [];
      const fail = (field, message) => errors.push({ field, message });

      if (this.retailerMode) {
        const retailer = this._val('[name="retailer_name"]');
        if (!retailer) fail('retailer_name', 'Please select a retailer.');
        if (retailer === 'Other' && !this._val('[name="retailer_other_name"]')) {
          fail('retailer_other_name', 'Please enter the retailer name.');
        }
        if (!this._val('[name="retailer_staff_name"]')) {
          fail('retailer_staff_name', 'Please enter the staff member name.');
        }
      }

      if (this.formState === 2 && !this.manualMode && !this.retailerMode) {
        if (!this.querySelector('[name="watch_selection"]:checked')) {
          fail('watch_selection', 'Please select which watch you are registering.');
        }
      }

      if (!this._val('[name="serial_number"]')) {
        fail('serial_number', 'Please enter your serial number.');
      }

      if (this._needsManualFields()) {
        const model = this._val('[name="model"]');
        if (!model) fail('model', 'Please select your watch model.');
        if (model === 'Older / other' && !this._val('[name="model_other"]')) {
          fail('model_other', 'Please specify your watch model.');
        }
        if (!this._val('[name="purchase_date"]')) {
          fail('purchase_date', 'Please enter when you received your watch.');
        }
        if (!this._val('[name="full_name"]')) {
          fail('full_name', 'Please enter your name.');
        }
        const email = this._val('[name="email"]');
        if (!email) {
          fail('email', 'Please enter your email address.');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          fail('email', 'Please enter a valid email address.');
        }
        if (!this._val('[name="country"]')) {
          fail('country', 'Please enter your country.');
        }
      }

      if (!this.querySelector('[name="terms"]').checked) {
        fail('terms', 'Please accept the warranty terms.');
      }

      if (!this.endpoint) {
        fail(null, 'Form is not configured. Please contact support.');
      }

      if (errors.length > 0) {
        this.showError(errors[0].message);
        this._markFields(errors.map((error) => error.field).filter(Boolean));
        return false;
      }

      return true;
    }

    _needsManualFields() {
      return this.retailerMode || this.manualMode || this.formState >= 3;
    }

    _val(selector) {
      const el = this.querySelector(selector);
      return el ? el.value.trim() : '';
    }

    _buildPayload(turnstileToken) {
      const isUsingPrefill = this.formState <= 2 && !this.manualMode && !this.retailerMode;

      const payload = {
        submission_id: this.submissionIdInput.value,
        turnstile_token: turnstileToken,
        mode: this.retailerMode ? 'retailer' : 'customer',
        serial_number: this._val('[name="serial_number"]'),
        marketing_consent: this.retailerMode ? false : (this.querySelector('[name="marketing_consent"]')?.checked || false),
      };

      if (this.retailerMode) {
        payload.retailer_name = this._val('[name="retailer_name"]');
        if (payload.retailer_name === 'Other') {
          payload.retailer_other_name = this._val('[name="retailer_other_name"]');
        }
        payload.retailer_staff_name = this._val('[name="retailer_staff_name"]');
      }

      if (isUsingPrefill) {
        payload.model = this.querySelector('[data-prefill="model"]')?.value || '';
        payload.purchase_date = this.querySelector('[data-prefill="purchase_date"]')?.value || '';
        payload.full_name = this.querySelector('[data-prefill="full_name"]')?.value || '';
        payload.email = this.querySelector('[data-prefill="email"]')?.value || '';
        payload.country = this.querySelector('[data-prefill="country"]')?.value || '';
        payload.phone = this.querySelector('[data-prefill="phone"]')?.value || '';
        payload.address = this.querySelector('[data-prefill="address"]')?.value || '';

        // Order reference for the delivery-date lookup (STR-923). Only sent
        // when it matches the Edge Function's accepted shape, so an odd
        // order-name format can never fail the registration.
        const orderRef = this.querySelector('[data-prefill="shopify_order_ref"]')?.value || '';
        if (/^#?[A-Za-z0-9-]{1,32}$/.test(orderRef)) {
          payload.shopify_order_ref = orderRef;
        }
      } else {
        payload.model = this._val('[name="model"]');
        if (payload.model === 'Older / other') {
          payload.model_other = this._val('[name="model_other"]');
        }
        payload.purchase_date = this._val('[name="purchase_date"]');
        payload.full_name = this._val('[name="full_name"]');
        payload.email = this._val('[name="email"]');
        payload.country = this._val('[name="country"]');
        payload.phone = this._val('[name="phone"]') || '';
        payload.address = this._val('[name="address"]') || '';
      }

      const customerIdInput = this.querySelector('[name="shopify_customer_id"]');
      if (customerIdInput && !this.retailerMode) {
        payload.shopify_customer_id = customerIdInput.value;
      }

      return payload;
    }

    // --- UI feedback ---

    _setLoading(loading) {
      if (loading) {
        this.submitBtn.disabled = true;
        this.submitBtn.setAttribute('aria-busy', 'true');
        this._originalButtonText = this.submitBtn.textContent;
        this.submitBtn.textContent = 'Registering…';
      } else {
        this.submitBtn.disabled = false;
        this.submitBtn.removeAttribute('aria-busy');
        this.submitBtn.textContent = this._originalButtonText || 'Register watch';
      }
    }

    showError(message) {
      this.showErrors([message]);
    }

    showErrors(messages) {
      this.errorsEl.replaceChildren(
        ...messages.map((message) => {
          const line = document.createElement('div');
          line.textContent = message;
          return line;
        })
      );
      this.errorsEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    clearError() {
      this.errorsEl.textContent = '';
      this._clearFieldErrors();
    }

    _showSuccess(email) {
      this.formArea.hidden = true;
      const emailEl = this.querySelector('[data-success-email]');
      if (emailEl) emailEl.textContent = email;
      this.successEl.hidden = false;
      this.successEl.focus();
      this._clearDraft();
    }

    // --- Draft persistence ---

    _createDebouncedSave() {
      return () => {
        clearTimeout(this._saveDraftTimer);
        this._saveDraftTimer = setTimeout(() => this._saveDraft(), 500);
      };
    }

    _saveDraft() {
      if (sessionStorage.getItem('straum_warranty_submitted')) return;

      const draft = {
        submission_id: this.submissionIdInput.value,
        serial_number: this._val('[name="serial_number"]'),
        retailer_mode: this.retailerMode,
        manual_mode: this.manualMode,
      };

      if (this._needsManualFields()) {
        draft.model = this._val('[name="model"]');
        draft.model_other = this._val('[name="model_other"]');
        draft.purchase_date = this._val('[name="purchase_date"]');
        draft.full_name = this._val('[name="full_name"]');
        draft.email = this._val('[name="email"]');
        draft.country = this._val('[name="country"]');
        draft.phone = this._val('[name="phone"]');
        draft.address = this._val('[name="address"]');
      }

      if (this.retailerMode) {
        draft.retailer_name = this._val('[name="retailer_name"]');
        draft.retailer_other_name = this._val('[name="retailer_other_name"]');
        draft.retailer_staff_name = this._val('[name="retailer_staff_name"]');
      }

      if (this.formState === 2) {
        const checked = this.querySelector('[name="watch_selection"]:checked');
        if (checked) draft.watch_selection = checked.value;
      }

      draft.marketing_consent = this.querySelector('[name="marketing_consent"]')?.checked || false;
      draft.terms = this.querySelector('[name="terms"]')?.checked || false;

      try {
        sessionStorage.setItem('straum_warranty_draft', JSON.stringify(draft));
      } catch (_) {
        // sessionStorage full or unavailable
      }
    }

    _loadDraft() {
      if (sessionStorage.getItem('straum_warranty_submitted')) return null;
      try {
        const raw = sessionStorage.getItem('straum_warranty_draft');
        return raw ? JSON.parse(raw) : null;
      } catch (_) {
        return null;
      }
    }

    _restoreDraft(draft) {
      if (draft.serial_number) {
        const el = this.querySelector('[name="serial_number"]');
        if (el) el.value = draft.serial_number;
      }

      if (draft.retailer_mode) this._enterRetailerMode();
      if (draft.manual_mode && !draft.retailer_mode) this.enterManualMode();

      if (draft.model) {
        const el = this.querySelector('[name="model"]');
        if (el) {
          el.value = draft.model;
          el.dispatchEvent(new Event('change'));
        }
      }
      if (draft.model_other) {
        const el = this.querySelector('[name="model_other"]');
        if (el) el.value = draft.model_other;
      }
      if (draft.purchase_date) {
        const el = this.querySelector('[name="purchase_date"]');
        if (el) el.value = draft.purchase_date;
      }
      if (draft.full_name) {
        const el = this.querySelector('[name="full_name"]');
        if (el) el.value = draft.full_name;
      }
      if (draft.email) {
        const el = this.querySelector('[name="email"]');
        if (el) el.value = draft.email;
      }
      if (draft.country) {
        const el = this.querySelector('[name="country"]');
        if (el) el.value = draft.country;
      }
      if (draft.phone) {
        const el = this.querySelector('[name="phone"]');
        if (el) el.value = draft.phone;
      }
      if (draft.address) {
        const el = this.querySelector('[name="address"]');
        if (el) el.value = draft.address;
      }

      if (draft.retailer_name) {
        const el = this.querySelector('[name="retailer_name"]');
        if (el) {
          el.value = draft.retailer_name;
          el.dispatchEvent(new Event('change'));
        }
      }
      if (draft.retailer_other_name) {
        const el = this.querySelector('[name="retailer_other_name"]');
        if (el) el.value = draft.retailer_other_name;
      }
      if (draft.retailer_staff_name) {
        const el = this.querySelector('[name="retailer_staff_name"]');
        if (el) el.value = draft.retailer_staff_name;
      }

      if (draft.watch_selection != null && this.formState === 2) {
        const radio = this.querySelector(`[name="watch_selection"][value="${CSS.escape(draft.watch_selection)}"]`);
        if (radio) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change'));
        }
      }

      if (draft.marketing_consent) {
        const el = this.querySelector('[name="marketing_consent"]');
        if (el) el.checked = true;
      }
      if (draft.terms) {
        const el = this.querySelector('[name="terms"]');
        if (el) el.checked = true;
      }
    }

    _clearDraft() {
      sessionStorage.removeItem('straum_warranty_draft');
      sessionStorage.setItem('straum_warranty_submitted', 'true');
    }
  }

  customElements.define('warranty-register', WarrantyRegister);
}
